import { useState, useEffect } from 'react';
import { networkService } from '../services';

/**
 * Hook to track online/offline status of the application.
 * 
 * @returns An object containing:
 *   - isOnline: boolean indicating if the app is currently online
 *   - wasOffline: boolean indicating if the app was offline since the component mounted
 *   - lastOfflineAt: timestamp of when the app last went offline, or null if it hasn't been offline
 *   - lastOnlineAt: timestamp of when the app last came online, or null if it hasn't come online since being offline
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState<boolean>(networkService.isOnline());
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [lastOfflineAt, setLastOfflineAt] = useState<number | null>(null);
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(null);

  useEffect(() => {
    // Use NetworkService for more reliable tracking
    const unsubscribe = networkService.addListener((online) => {
      if (online && !isOnline) {
        // Transition from offline to online
        setIsOnline(true);
        setLastOnlineAt(Date.now());
      } else if (!online && isOnline) {
        // Transition from online to offline
        setIsOnline(false);
        setWasOffline(true);
        setLastOfflineAt(Date.now());
      } else {
        // Just update the status
        setIsOnline(online);
      }
    });

    // Cleanup
    return unsubscribe;
  }, [isOnline]);

  return {
    isOnline,
    wasOffline,
    lastOfflineAt,
    lastOnlineAt,
  };
};

export default useOnlineStatus;